<template>
  <div class="agreement-preview">
    <header class="agreement-head">
      <PrintHeader
        title="Holiday Schedule"
        :partyAName="partyAName"
        :partyBName="partyBName"
        :subtitle="jurisdictionLabel"
      />
      <p class="agreement-intro">
        The parties agree that {{ childrenIntro }} will spend time with each parent on
        the holidays, special days, and school breaks listed below, according to the
        arrangements set out for each occasion.
      </p>
      <p v-if="agedOutNote" class="aged-out-note">{{ agedOutNote }}</p>
      <p v-if="discussionCount > 0" class="agreement-discuss-banner">
        <strong>{{ discussionCount }} {{ discussionCount === 1 ? 'item is' : 'items are' }} still under discussion.</strong>
        Marked items below are not yet finalized and will need to be agreed before this
        schedule is signed.
      </p>
    </header>

    <section v-for="cat in groupedClauses" :key="cat.key" class="agreement-section">
      <h2>{{ cat.label }}</h2>
      <ol class="clauses">
        <li v-for="c in cat.clauses" :key="c.id">
          {{ c.prose }}<span v-if="c.flagged" class="discuss-tag"> (under discussion)</span>
        </li>
      </ol>
    </section>

    <p v-if="!groupedClauses.length" class="empty-state">
      No arrangements decided yet. Configure holidays on the left to populate the agreement.
    </p>
  </div>
</template>

<script>
import PrintHeader from '../../PrintHeader.vue'
import { renderClause, CATEGORY_ORDER, UNDECIDED_KINDS } from '@/lib/parentingSchedule/proseRenderer'
import { CATEGORIES } from '@/lib/parentingSchedule/seed/holidays'
import { childrenSubject, agedOutNote } from '@/lib/parentingSchedule/children'
import { PROVINCE_NAMES } from '@/lib/holidayScheduleProvince'

function isUndecided(h) {
  return UNDECIDED_KINDS.has(h?.rule?.kind)
}

export default {
  name: 'AgreementPreview',
  components: { PrintHeader },
  props: {
    holidays: { type: Array, required: true },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    jurisdiction: { type: String, default: '' },
    children: { type: Array, default: () => [] },
  },
  computed: {
    jurisdictionLabel() {
      return PROVINCE_NAMES[this.jurisdiction] || this.jurisdiction
    },
    childrenIntro() {
      return childrenSubject(this.children, this.jurisdiction, { fallback: 'their children' })
    },
    // On-screen transparency when a child ages out of the wording (never
    // printed): explains why the clauses name fewer children.
    agedOutNote() {
      return agedOutNote(this.children, this.jurisdiction)
    },
    groupedClauses() {
      const parties = {
        partyAName: this.partyAName,
        partyBName: this.partyBName,
        children: this.children,
        jurisdiction: this.jurisdiction,
      }
      const byCategory = {}
      for (const h of this.holidays) {
        // Skip undecided rows — they belong in the editor, not the agreement.
        if (isUndecided(h)) continue
        const list = (byCategory[h.category] ||= [])
        list.push({
          id: h.id,
          prose: renderClause(h, parties),
          flagged: !!h.flaggedForDiscussion,
        })
      }
      return CATEGORY_ORDER
        .filter((key) => byCategory[key]?.length)
        .map((key) => ({ key, label: CATEGORIES[key], clauses: byCategory[key] }))
    },
    // Count of clauses (i.e. decided rows) that are also flagged for discussion.
    // Drives the banner at the top of the preview / printed PDF.
    discussionCount() {
      let n = 0
      for (const cat of this.groupedClauses) {
        for (const c of cat.clauses) if (c.flagged) n++
      }
      return n
    },
  },
}
</script>

<style scoped>
.agreement-preview {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.75rem 1.85rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #1e293b;
}

.agreement-intro {
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
  color: #475569;
  border-left: 3px solid #a3bbc1;
  padding: 0.4rem 0.85rem;
  background: #f8fafc;
  border-radius: 0 6px 6px 0;
}

/* Screen-only explanation when an adult child drops out of the wording.
   Hidden in print — the agreement itself shouldn't carry app commentary. */
.aged-out-note {
  margin: 0 0 1.25rem;
  font-size: 0.8rem;
  color: #85581a;
  border-left: 3px solid #f7a91b;
  padding: 0.4rem 0.85rem;
  background: #fef6e0;
  border-radius: 0 6px 6px 0;
}

/* Banner shown when at least one decided clause is flagged for discussion.
   Honest signal on a printed draft taken to mediation: this isn't final yet. */
.agreement-discuss-banner {
  margin: 0 0 1.25rem;
  font-size: 0.85rem;
  color: #85581a;
  border-left: 3px solid #f7a91b;
  padding: 0.5rem 0.85rem;
  background: #fef6e0;
  border-radius: 0 6px 6px 0;
}
.agreement-discuss-banner strong { color: #85581a; }

/* Inline tag appended to a clause that's flagged for discussion. */
.discuss-tag {
  font-style: italic;
  color: #85581a;
  font-size: 0.85em;
  font-weight: 500;
}

.empty-state {
  margin: 1.5rem 0 0.5rem;
  padding: 1rem;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  text-align: center;
  color: #64748b;
  font-size: 0.85rem;
  font-style: italic;
}

.agreement-section { margin-top: 1.25rem; }
.agreement-section h2 {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #3f6772;
  font-weight: 700;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.35rem;
}
.clauses {
  margin: 0;
  padding-left: 1.4rem;
}
.clauses li {
  margin-bottom: 0.5rem;
}

/* Print: render only the agreement (the right-side preview) on 8.5×11 letter.
   Keeps the rendered prose identical to the live side panel — no editor controls,
   no buttons, no rotation table. Hooks live in HolidayBuilder.vue's print rules. */
@media print {
  .aged-out-note { display: none !important; }

  .agreement-preview {
    border: none;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    overflow: visible;
    /* Slightly smaller body font + tighter line spacing so 24 clauses
       compress comfortably onto fewer pages without feeling cramped. */
    font-size: 0.8rem;
    line-height: 1.35;
  }

  /* Pagination strategy:
     - Sections (Statutory, Special, etc.) CAN break across pages — long
       sections shouldn't get pushed wholesale to page 2.
     - Section headings stick with the first clause underneath them
       (no orphan headings at the bottom of a page).
     - Individual clauses never split mid-sentence across pages. */
  .agreement-section h2 {
    page-break-after: avoid;
    break-after: avoid;
  }
  .clauses li {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .agreement-intro {
    font-size: 0.78rem;
    line-height: 1.35;
    margin-bottom: 0.7rem;
    padding: 0.3rem 0.7rem;
  }
  .agreement-discuss-banner {
    font-size: 0.78rem;
    line-height: 1.35;
    margin-bottom: 0.7rem;
    padding: 0.35rem 0.7rem;
  }
  .agreement-section { margin-top: 0.7rem; }
  .agreement-section h2 {
    font-size: 0.85rem;
    margin: 0 0 0.35rem;
    padding-bottom: 0.25rem;
  }
  .clauses { margin: 0; padding-left: 1.2rem; }
  .clauses li { margin-bottom: 0.25rem; }
}
</style>
